export type OpenAnalystSandboxState = {
  state?: string
  status?: string
  progress?: number
  progressMessage?: string
  message?: string
  routeToken?: string
  routeTokenExpiresAt?: string
  connectUrl?: string
}

export type OpenAnalystSandboxReady = OpenAnalystSandboxState & {
  state?: "ready"
  status?: "ready"
  routeTokenExpiresAt: string
  connectUrl: string
}

export type OpenAnalystSandboxClient = {
  provision(): Promise<OpenAnalystSandboxState>
  status(): Promise<OpenAnalystSandboxState>
  reconnect(): Promise<OpenAnalystSandboxState>
  heartbeat(): Promise<OpenAnalystSandboxState>
  stop(): Promise<OpenAnalystSandboxState>
  info(): Promise<unknown>
  volumeStatus(): Promise<unknown>
}

export class OpenAnalystSandboxError extends Error {
  status: number
  code?: string
  path?: string
  requestId?: string

  constructor(status: number, message: string, details?: { code?: string; path?: string; requestId?: string }) {
    super(message)
    this.name = "OpenAnalystSandboxError"
    this.status = status
    this.code = details?.code
    this.path = details?.path
    this.requestId = details?.requestId
  }
}

export class OpenAnalystSandboxAuthError extends OpenAnalystSandboxError {
  constructor() {
    super(401, "OpenAnalyst sign-in is required before provisioning a hosted runtime.")
    this.name = "OpenAnalystSandboxAuthError"
  }
}

type SandboxClientInput = {
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  apiBaseUrl: string
  source: "desktop" | "web" | "api"
  getAccessToken?: () => string | null
  refreshAccessToken?: () => Promise<string | null>
}

type ErrorPayload = {
  error?:
    | string
    | {
        code?: string
        message?: string
      }
  code?: string
  message?: string
  detail?: string
}

type SandboxErrorInfo = {
  code?: string
  message: string
  path: string
  requestId: string
}

const forbiddenCredentialFields = new Set([
  "accesskeyid",
  "awsaccesskeyid",
  "awssecretaccesskey",
  "credential",
  "credentials",
  `${"day"}tonajwt`,
  `${"day"}tonatoken`,
  "providercredential",
  "providercredentials",
  "providersecret",
  "sandboxjwt",
  "sandboxtoken",
  "secretaccesskey",
])

async function parseJson(response: Response) {
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return { message: text } satisfies ErrorPayload
  }
}

function createSandboxRequestId() {
  return `oa_sandbox_${globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`
}

function responseRequestId(response: Response, fallback: string) {
  return (
    response.headers.get("x-openanalyst-request-id") ||
    response.headers.get("x-request-id") ||
    response.headers.get("cf-ray") ||
    fallback
  )
}

function sandboxAction(path: string) {
  if (path === "/provision") return "provisioning Daytona runtime"
  if (path === "/status") return "checking Daytona runtime status"
  if (path === "/reconnect") return "reconnecting Daytona runtime"
  return "calling the hosted sandbox backend"
}

function sandboxErrorInfo(payload: unknown, status: number, path: string, requestId: string): SandboxErrorInfo {
  if (!payload || typeof payload !== "object") {
    return {
      message: `Sandbox request failed with status ${status}`,
      path,
      requestId,
    }
  }
  const data = payload as ErrorPayload
  const error = data.error && typeof data.error === "object" ? data.error : undefined
  const code = error?.code || data.code
  const message =
    (typeof data.error === "string" ? data.error : error?.message) ||
    data.message ||
    data.detail ||
    `Sandbox request failed with status ${status}`
  const genericMessage = message === "An unexpected error occurred" || message === `Sandbox request failed with status ${status}`
  if (
    status >= 500 &&
    (path === "/provision" || path === "/status" || path === "/reconnect") &&
    genericMessage
  ) {
    return {
      code,
      message: `OpenAnalyst cloud sandbox backend failed while ${sandboxAction(path)}. Request ID: ${requestId}.`,
      path,
      requestId,
    }
  }
  return { code, message, path, requestId }
}

function containsForbiddenCredential(value: unknown): boolean {
  if (!value || typeof value !== "object") return false
  if (Array.isArray(value)) return value.some(containsForbiddenCredential)
  return Object.entries(value).some(([key, item]) => {
    if (forbiddenCredentialFields.has(key.toLowerCase())) return true
    return containsForbiddenCredential(item)
  })
}

function ready(data: OpenAnalystSandboxState) {
  return data.state === "ready" || data.status === "ready"
}

function normalizeReady(data: OpenAnalystSandboxState): OpenAnalystSandboxState {
  if (!ready(data)) return data
  if (typeof data.connectUrl !== "string" || !data.connectUrl.trim()) {
    throw new Error("Ready sandbox response did not include connectUrl")
  }
  if (typeof data.routeTokenExpiresAt !== "string" || !data.routeTokenExpiresAt.trim()) {
    throw new Error("Ready sandbox response did not include routeTokenExpiresAt")
  }
  return data
}

export function createOpenAnalystSandboxClient(input: SandboxClientInput): OpenAnalystSandboxClient {
  const apiBaseUrl = input.apiBaseUrl.replace(/\/+$/, "").replace(/\/api\/v1$/, "")
  const url = (path: string) => `${apiBaseUrl}/api/v1/sandbox${path}`

  const request = async (path: string, method: "GET" | "POST") => {
    const accessToken = input.getAccessToken?.() ?? (await input.refreshAccessToken?.()) ?? null
    if (input.source === "api" && !accessToken) throw new OpenAnalystSandboxAuthError()
    const requestId = createSandboxRequestId()

    const sandboxFetch = (accessToken: string | null) => {
      const headers = new Headers()
      headers.set("Accept", "application/json")
      headers.set("x-auth-source", input.source)
      headers.set("x-openanalyst-request-id", requestId)
      if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)
      return input.fetch(url(path), {
        method,
        credentials: "include",
        headers,
      })
    }
    const first = await sandboxFetch(accessToken)
    const response =
      (first.status === 401 || first.status === 403) && input.refreshAccessToken
        ? await input.refreshAccessToken().then((refreshed) =>
            refreshed && refreshed !== accessToken ? sandboxFetch(refreshed) : first,
          )
        : first
    const payload = await parseJson(response)
    if (!response.ok) {
      const info = sandboxErrorInfo(payload, response.status, path, responseRequestId(response, requestId))
      throw new OpenAnalystSandboxError(response.status, info.message, info)
    }
    if (containsForbiddenCredential(payload)) throw new Error("Sandbox response exposed forbidden credential field")
    return normalizeReady((payload ?? {}) as OpenAnalystSandboxState)
  }

  return {
    provision: () => request("/provision", "POST"),
    status: () => request("/status", "GET"),
    reconnect: () => request("/reconnect", "POST"),
    heartbeat: () => request("/heartbeat", "POST"),
    stop: () => request("/stop", "POST"),
    info: () => request("/info", "GET"),
    volumeStatus: () => request("/volume-status", "GET"),
  }
}
